/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import { AxiosResponse, AxiosInstance } from 'axios';

import { datadogRum } from '@datadog/browser-rum';
import { CommonAPI } from '../common/responses';

const baseUrl = `/api/internal/rooms`;

export namespace RoomsAPI {
  export type CallDetailsResponse = CommonAPI.CallDetailsResponse;
  export type RoomDetailsResponse = CommonAPI.RoomDetailsResponse;
  export interface BrowserLogRequest {
    preflight: boolean;
    browserTime: Date;
    level: string;
    message: string;
    data?: any;
  }

  export interface ParticipantRoomMetadataResponse {
    signAheadDocsStatus: 'PENDING_BORROWER_SIGNATURE' | 'SIGNED' | 'NOT_AVAILABLE' | null;
    eNoteStatus: 'COMPLETE' | 'READY_TO_SIGN' | 'SIGNER_WITHDRAWN' | 'INVALID' | null;
    sentSignAheadDocsToRON: boolean | null;
    signENoteInRON: boolean | null;
    specialInstructions: string | null;
  }

  export interface DeviceTestingConfigResponse {
    videoAuthToken: string;
    videoProvider: string;
    testSessionId: string;
  }
}

export const roomsClient = (request: AxiosInstance) => ({
  async ensureSession(roomId: string): Promise<AxiosResponse> {
    return request.post(`${baseUrl}/${roomId}/sessions`);
  },

  async fetchCallDetailsForRoom(roomId: string): Promise<RoomsAPI.CallDetailsResponse> {
    return (await request.get(`${baseUrl}/${roomId}/call`)).data;
  },

  async fetchRoomDetail(roomId: string): Promise<RoomsAPI.RoomDetailsResponse> {
    return (await request.get(`${baseUrl}/${roomId}`)).data;
  },

  async sendBrowserLog(log: Partial<RoomsAPI.BrowserLogRequest>): Promise<void> {
    const message = log.message;
    // Ignore twilio [Connect #] spam logs
    if (message && !message.startsWith('[Connect')) {
      datadogRum.addAction(`Send browser-log: ${log.message}`);
    }
    await request.post(`${baseUrl}/browser-log`, {
      preflight: false,
      browserTime: new Date(),
      level: 'info',
      ...log,
    });
  },

  async fetchRoomMetadata(roomId: string): Promise<RoomsAPI.ParticipantRoomMetadataResponse> {
    return (await request.get(`${baseUrl}/${roomId}/metadata`)).data;
  },

  async fetchDeviceTestingConfig(roomId: string): Promise<RoomsAPI.DeviceTestingConfigResponse> {
    return (await request.get(`${baseUrl}/${roomId}/device-testing-config`)).data;
  },
});
